import { createContext, use, useState } from 'react';
import { AuthContext, DBContext, StorageContext } from './context';
import { readFromCache, saveToCache, clearAllCacheEntries } from './utils/cache-utils';
import { fetchBundleFromStorage } from './utils/storage-bulk-data';
import { startTrace, stopTrace } from './analytics';
import { appId } from './constants';
import { DBCollection } from '@/lib/utils/constants-shared';
import { DEFAULT_VIEW } from './utils/bible-utils';
import { fetchPassagesFromDB } from '@/data/passage/passage-data-service';
import { PassageDataProvider } from '@/data/passage/passage-data-context';
import { fetchOutlinesFromDB } from '@/data/outline/outline-data-service';
import { OutlineDataProvider } from '@/data/outline/outline-data-context';
import { fetchLiteraryFeaturesFromDB } from '@/data/literary-features/literary-feature-data-service';
import { fetchLiteraryFeaturesByPassageFromDB } from '@/data/literary-features/literary-features-by-passage-fetch';
import { LiteraryFeaturesDataProvider } from '@/data/literary-features/literary-feature-data-context';
import { LiteraryFeaturesByPassageDataProvider } from '@/data/literary-features/literary-features-by-passage-data-context';
import { fetchTranslationsFromDB } from '@/data/translation/translation-data-service';
import { TranslationDataProvider } from '@/data/translation/translation-data-context';
import { CultureByPassageDataProvider } from '@/data/culture/culture-by-passage-data-context';
import { fetchCultureByBookFromDB } from '@/data/culture/culture-by-book-fetch';
import { fetchCultureByPassageFromDB } from '@/data/culture/culture-by-passage-fetch';
import { CultureByBookDataProvider } from '@/data/culture/culture-by-book-data-context';
import { fetchArchaeologyByBookFromDB } from '@/data/archaeology/archaeology-by-book-fetch';
import { fetchArchaeologyByPassageFromDB } from '@/data/archaeology/archaeology-by-passage-fetch';
import { ArchaeologyByBookDataProvider } from '@/data/archaeology/archaeology-by-book-data-context';
import { ArchaeologyByPassageDataProvider } from '@/data/archaeology/archaeology-by-passage-data-context';
import { fetchPlacesFromDB } from '@/data/places/places-fetch';
import { PlacesDataProvider } from '@/data/places/places-data-context';
import { fetchPlacesByPassageFromDB } from '@/data/places/places-by-passage-fetch';
import { PlacesByPassageDataProvider } from '@/data/places/places-by-passage-data-context';
import { fetchHistoricalEventsFromDB } from '@/data/historical-events/historical-events-fetch';
import { HistoricalEventsDataProvider } from '@/data/historical-events/historical-events-data-context';
import { fetchHistoricalEventsByBookFromDB } from '@/data/historical-events/historical-events-by-book-fetch';
import { fetchHistoricalEventsByPassageFromDB } from '@/data/historical-events/historical-events-by-passage-fetch';
import { HistoricalEventsByBookDataProvider } from '@/data/historical-events/historical-events-by-book-data-context';
import { HistoricalEventsByPassageDataProvider } from '@/data/historical-events/historical-events-by-passage-data-context';
import { fetchPeopleFromDB } from '@/data/people/people-fetch';
import { fetchPeopleByPassageFromDB } from '@/data/people/people-by-passage-fetch';
import { PeopleDataProvider } from '@/data/people/people-data-context';
import { PeopleByPassageDataProvider } from '@/data/people/people-by-passage-data-context';
import { fetchStudyGuideByPassageFromDB } from '@/data/study-guide/study-guide-by-passage-fetch';
import { StudyGuideByPassageDataProvider } from '@/data/study-guide/study-guide-by-passage-data-context';
import { GuidedStudyByPassageDataProvider } from '@/data/study-guide/guided-study-by-passage-data-context';
import { FootnoteDataProvider } from '@/data/footnotes/footnote-data-context';



export enum HydrationStage {
    Initial = 0,      // Nothing loaded
    Rendering = 1,    // translations + passages loaded
    SearchReady = 2,  // literaryFeaturesByPassage + people + places + historicalEvents loaded
    Full = 3          // All eager data loaded
}

interface AppDataContextType {
    isLoaded$: Promise<void>;
    hydrationStage: HydrationStage;
    isUpdateAvailable: boolean;
    data?: {
        archaeologyByBook: ArchaeologyByBook[];
        archaeologyByPassage: ArchaeologyByPassage[];
        cultureByPassage: CultureByPassage[];
        cultureByBook: CultureByBook[];
        historicalEvents: HistoricalEvent[];
        historicalEventsByBook: HistoricalEventsByBook[];
        historicalEventsByPassage: HistoricalEventsByPassage[];
        literaryFeatures: LiteraryFeaturesByBook[];
        literaryFeaturesByPassage: LiteraryFeaturesByPassage[];
        outlines: Outline[];
        passages: Passage[];
        people: Person[];
        peopleByPassage: PeopleByPassage[];
        studyGuideByPassage: StudyGuideByPassage[];

        places: Place[];
        placesByPassage: PlacesByPassage[];
        translations: TranslationByParagraph[];
    };
    refresh: () => Promise<void>;
}

export const AppDataContext = createContext<AppDataContextType>(undefined);

export function AppDataHydrationProvider({ children }) {
    const auth = use(AuthContext);
    const db = use(DBContext);
    const storage = use(StorageContext);
    const [hydrationStage, setHydrationStage] = useState<HydrationStage>(HydrationStage.Initial);
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const [isLoaded$, setIsLoaded$] = useState<Promise<void>>(fetchData);
    const [data, setData] = useState<AppDataContextType['data']>();

    async function fetchData(skipCache = false) {
        const totalTrace = startTrace('app_hydration_total');
        let stage1Trace = startTrace('hydration_stage_1');
        let cacheStatus: 'cold' | 'warm' = 'cold';

        // Helper to check version after loading (Phase 5)
        const checkVersion = async () => {
            if (skipCache) return;
            try {
                // Since bulk-data is now public, use deterministic URL to bypass handshake
                const bucket = 'bible-glen.firebasestorage.app';
                const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/bulk-data%2Fdata-version.json?alt=media`;

                const response = await fetch(url, { cache: 'no-cache' });
                const remoteVersion = await response.json();

                const localVersion = localStorage.getItem('bible-glen-data-version');
                if (localVersion && localVersion !== remoteVersion.version) {
                    setIsUpdateAvailable(true);
                    await clearAllCacheEntries(appId);
                    localStorage.setItem('bible-glen-data-version', remoteVersion.version);
                } else if (!localVersion) {
                    localStorage.setItem('bible-glen-data-version', remoteVersion.version);
                }
            } catch (err) {
                console.warn('[Hydration] Version check failed:', err);
            }
        };

        try {
            const getTranslations = async (skipCache: boolean) => {
                if (!skipCache) {
                    const cached = await readFromCache<TranslationByParagraph[]>(appId, DBCollection.translations);
                    if (cached && cached.length > 0) {
                        cacheStatus = 'warm';
                        return cached;
                    }
                }
                const result = await fetchTranslationsFromDB([DEFAULT_VIEW.version], storage);
                return result;
            };

            // STAGE 1: Render-critical (Translations + Passages)
            const [translations, passages] = await Promise.all([
                getTranslations(skipCache),
                (async () => {
                    if (!skipCache) {
                        const cached = await readFromCache<Passage[]>(appId, DBCollection.passages);
                        if (cached) {
                            cacheStatus = 'warm';
                            return cached;
                        }
                    }
                    const result = await fetchPassagesFromDB(auth, db, storage);
                    return result;
                })()
            ]);

            setData(prev => ({ ...prev, translations, passages } as AppDataContextType['data']));
            setHydrationStage(HydrationStage.Rendering);
            stopTrace(stage1Trace, { cache_status: cacheStatus, skip_cache: skipCache.toString() });

            // STAGE 2: Search-ready & Book Context (People, Places, Historical Events, Literary Features, ArchaeologyByBook, CultureByBook, Outlines)
            let stage2Trace = startTrace('hydration_stage_2');

            const bundle = await fetchBundleFromStorage<any>(
                'bulk-data/bundle-search-ready.json',
                async () => {
                    // Fallback to individual parallel fetches if bundle fails
                    const [p, pl, h, l, a, c, hb, o] = await Promise.all([
                        fetchPeopleFromDB(storage),
                        fetchPlacesFromDB(storage),
                        fetchHistoricalEventsFromDB(storage),
                        fetchLiteraryFeaturesFromDB(auth, db),
                        fetchArchaeologyByBookFromDB(),
                        fetchCultureByBookFromDB(),
                        fetchHistoricalEventsByBookFromDB(),
                        fetchOutlinesFromDB(auth, db)
                    ]);
                    return {
                        [DBCollection.people]: p,
                        [DBCollection.places]: pl,
                        [DBCollection.historicalEvents]: h,
                        [DBCollection.literaryFeaturesByBook]: l,
                        [DBCollection.archaeologyByBook]: a,
                        [DBCollection.cultureByBook]: c,
                        [DBCollection.historicalEventsByBook]: hb,
                        [DBCollection.outlines]: o
                    };
                }
            );

            // Re-map bundle back to state pieces
            const people = bundle[DBCollection.people];
            const places = bundle[DBCollection.places];
            const historicalEvents = bundle[DBCollection.historicalEvents];
            const literaryFeatures = bundle[DBCollection.literaryFeaturesByBook];
            const archaeologyByBook = bundle[DBCollection.archaeologyByBook];
            const cultureByBook = bundle[DBCollection.cultureByBook];
            const historicalEventsByBook = bundle[DBCollection.historicalEventsByBook];
            const outlines = bundle[DBCollection.outlines];

            setData(prev => ({
                ...prev,
                people,
                places,
                historicalEvents,
                literaryFeatures,
                archaeologyByBook,
                cultureByBook,
                historicalEventsByBook,
                outlines
            } as AppDataContextType['data']));
            setHydrationStage(HydrationStage.SearchReady);
            stopTrace(stage2Trace, { cache_status: cacheStatus, skip_cache: skipCache.toString() });

            // STAGE 3: Full Context Data (All ByPassage collections) - Parallel Fetch
            let stage3Trace = startTrace('hydration_stage_3');

            const [
                archaeologyByPassage,
                cultureByPassage,
                historicalEventsByPassage,
                literaryFeaturesByPassage,
                peopleByPassage,
                placesByPassage,
                studyGuideByPassage
            ] = await Promise.all([
                fetchArchaeologyByPassageFromDB(auth, db, storage),
                fetchCultureByPassageFromDB(auth, db, storage),
                fetchHistoricalEventsByPassageFromDB(auth, db, storage),
                fetchLiteraryFeaturesByPassageFromDB(auth, db, storage),
                fetchPeopleByPassageFromDB(auth, db, storage),
                fetchPlacesByPassageFromDB(auth, db, storage),
                fetchStudyGuideByPassageFromDB(auth, db, storage)
            ]);

            setData(prev => {
                const merge = <T extends { id: string }>(initial: T[] | undefined, bulk: T[] | undefined) => {
                    if (!bulk) return initial || [];
                    const map = new Map<string, T>();
                    bulk.forEach(item => map.set(item.id, item));
                    initial?.forEach(item => map.set(item.id, item));
                    return Array.from(map.values());
                };

                return {
                    ...prev,
                    archaeologyByPassage: merge(prev?.archaeologyByPassage, archaeologyByPassage),
                    cultureByPassage: merge(prev?.cultureByPassage, cultureByPassage),
                    historicalEventsByPassage: merge(prev?.historicalEventsByPassage, historicalEventsByPassage),
                    literaryFeaturesByPassage: merge(prev?.literaryFeaturesByPassage, literaryFeaturesByPassage),
                    peopleByPassage: merge(prev?.peopleByPassage, peopleByPassage),
                    placesByPassage: merge(prev?.placesByPassage, placesByPassage),
                    studyGuideByPassage: merge(prev?.studyGuideByPassage, studyGuideByPassage)
                } as AppDataContextType['data'];
            });

            setHydrationStage(HydrationStage.Full);
            stopTrace(stage3Trace, { cache_status: cacheStatus, skip_cache: skipCache.toString() });
            stopTrace(totalTrace, { cache_status: cacheStatus, skip_cache: skipCache.toString() });

            // Save loaded collections to cache
            saveToCache(appId, DBCollection.translations, translations);
            saveToCache(appId, DBCollection.passages, passages);
            saveToCache(appId, DBCollection.people, people);
            saveToCache(appId, DBCollection.places, places);
            saveToCache(appId, DBCollection.historicalEvents, historicalEvents);
            saveToCache(appId, DBCollection.literaryFeaturesByBook, literaryFeatures);
            saveToCache(appId, DBCollection.archaeologyByBook, archaeologyByBook);
            saveToCache(appId, DBCollection.cultureByBook, cultureByBook);
            saveToCache(appId, DBCollection.historicalEventsByBook, historicalEventsByBook);
            saveToCache(appId, DBCollection.outlines, outlines);

            // Context ByPassage cache (parallel background save)
            const currentData = (await (isLoaded$ as any))?.data || {};
            if (currentData.placesByPassage) saveToCache(appId, DBCollection.placesByPassage, currentData.placesByPassage);
            if (currentData.archaeologyByPassage) saveToCache(appId, DBCollection.archaeologyByPassage, currentData.archaeologyByPassage);
            if (currentData.cultureByPassage) saveToCache(appId, DBCollection.cultureByPassage, currentData.cultureByPassage);
            if (currentData.historicalEventsByPassage) saveToCache(appId, DBCollection.historicalEventsByPassage, currentData.historicalEventsByPassage);
            if (currentData.literaryFeaturesByPassage) saveToCache(appId, DBCollection.literaryFeaturesByPassage, currentData.literaryFeaturesByPassage);
            if (currentData.peopleByPassage) saveToCache(appId, DBCollection.peopleByPassage, currentData.peopleByPassage);
            if (currentData.studyGuideByPassage) saveToCache(appId, DBCollection.studyGuideByPassage, currentData.studyGuideByPassage);

            // Check for updates in the background after everything is loaded (Phase 5)
            checkVersion();
        }
        catch (err) {
            stopTrace(totalTrace, { status: 'error' });
            console.error('App Hydration Error:', err);
        }
    }

    async function refresh() {
        // Clear all cache entries (both main collections and lazy-loaded items)
        await clearAllCacheEntries(appId);

        setData(undefined);
        setIsLoaded$(fetchData(true).catch(() => setData(data)));
    }

    return <>
        <AppDataContext value={{ isLoaded$, hydrationStage, isUpdateAvailable, data, refresh }}>
            <TranslationDataProvider>
                <FootnoteDataProvider>
                    <PassageDataProvider>
                        <OutlineDataProvider>
                            <LiteraryFeaturesDataProvider literaryFeaturesByBook={data?.literaryFeatures || []}>
                                <LiteraryFeaturesByPassageDataProvider literaryFeaturesByPassage={data?.literaryFeaturesByPassage || []}>
                                    <CultureByPassageDataProvider cultureByPassage={data?.cultureByPassage || []}>
                                        <CultureByBookDataProvider cultureByBook={data?.cultureByBook || []}>
                                            <ArchaeologyByBookDataProvider archaeologyByBook={data?.archaeologyByBook || []}>
                                                <ArchaeologyByPassageDataProvider archaeologyByPassage={data?.archaeologyByPassage || []}>
                                                    <PeopleDataProvider people={data?.people || []}>
                                                        <PeopleByPassageDataProvider peopleByPassage={data?.peopleByPassage || []}>
                                                            <StudyGuideByPassageDataProvider studyGuideByPassage={data?.studyGuideByPassage || []}>
                                                                <GuidedStudyByPassageDataProvider guidedStudyByPassage={[]}>
                                                                    <PlacesDataProvider places={data?.places || []}>
                                                                        <PlacesByPassageDataProvider placesByPassage={data?.placesByPassage || []}>
                                                                            <HistoricalEventsDataProvider historicalEvents={data?.historicalEvents || []}>
                                                                                <HistoricalEventsByBookDataProvider historicalEventsByBook={data?.historicalEventsByBook || []}>
                                                                                    <HistoricalEventsByPassageDataProvider historicalEventsByPassage={data?.historicalEventsByPassage || []}>
                                                                                        {children}
                                                                                    </HistoricalEventsByPassageDataProvider>
                                                                                </HistoricalEventsByBookDataProvider>
                                                                            </HistoricalEventsDataProvider>
                                                                        </PlacesByPassageDataProvider>
                                                                    </PlacesDataProvider>
                                                                </GuidedStudyByPassageDataProvider>
                                                            </StudyGuideByPassageDataProvider>
                                                        </PeopleByPassageDataProvider>
                                                    </PeopleDataProvider>
                                                </ArchaeologyByPassageDataProvider>
                                            </ArchaeologyByBookDataProvider>
                                        </CultureByBookDataProvider>
                                    </CultureByPassageDataProvider>
                                </LiteraryFeaturesByPassageDataProvider>
                            </LiteraryFeaturesDataProvider>
                        </OutlineDataProvider>
                    </PassageDataProvider>
                </FootnoteDataProvider>
            </TranslationDataProvider>
        </AppDataContext>
    </>;

}
